import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PostsProvider } from './context/PostsContext';
import ProtectedRoute from './components/ProtectedRoute';
import RouteFallback from './components/RouteFallback';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Footer from './components/Layout/Footer';
import './App.css';

// Heavy routes — keep pdfjs, exceljs, and admin UI out of the Home bundle.
const Documents = React.lazy(() => import('./pages/Documents'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Unauthorized = React.lazy(() => import('./pages/Unauthorized'));
const AdminAnalytics = React.lazy(() => import('./pages/AdminAnalytics'));
const AdminActivityLog = React.lazy(() => import('./pages/AdminActivityLog'));
const AdminConfiguration = React.lazy(() => import('./pages/AdminConfiguration'));
const AdminLeads = React.lazy(() => import('./pages/AdminLeads'));
const Polls = React.lazy(() => import('./pages/Polls'));
const HR = React.lazy(() => import('./pages/departments/HR'));
const IT = React.lazy(() => import('./pages/departments/IT'));
const Finance = React.lazy(() => import('./pages/departments/Finance'));
const Sales = React.lazy(() => import('./pages/departments/Sales'));
const Service = React.lazy(() => import('./pages/departments/Service'));
const Marketing = React.lazy(() => import('./pages/departments/Marketing'));
const Admin = React.lazy(() => import('./pages/departments/Admin'));
const SpareParts = React.lazy(() => import('./pages/departments/SpareParts'));

const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<RouteFallback />}>{children}</Suspense>
);

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Redirect to dashboard if already logged in
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Documents />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/polls"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Polls />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'CO_ADMIN']}>
            <LazyPage>
              <UserManagement />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Profile />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <LazyPage>
              <AdminAnalytics />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']} allowedDepartments={['Sales']}>
            <LazyPage>
              <AdminLeads />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <LazyPage>
              <AdminActivityLog />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/configuration"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <LazyPage>
              <AdminConfiguration />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {/* Department routes */}
      <Route
        path="/departments/hr"
        element={
          <ProtectedRoute>
            <LazyPage>
              <HR />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/it"
        element={
          <ProtectedRoute>
            <LazyPage>
              <IT />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/finance"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Finance />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/sales"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Sales />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/service"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Service />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/marketing"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Marketing />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/admin"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Admin />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/spareparts"
        element={
          <ProtectedRoute>
            <LazyPage>
              <SpareParts />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      {/* 403 page */}
      <Route
        path="/unauthorized"
        element={
          <LazyPage>
            <Unauthorized />
          </LazyPage>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const showGlobalFooter = location.pathname !== '/login';

  return (
    <div className="App">
      <ScrollToTop />
      <AppRoutes />
      {showGlobalFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <PostsProvider>
          <AppContent />
        </PostsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
